import { ContactUs } from "@App/Components/ContactUs";
import { BulletPoint } from "@App/Components/common/BulletPoint";
import {
  BonusBenefitsIcon,
  CalendarCheckBenefitsIcon,
  CheckBenefitsIcon,
} from "@App/Components/common/Icons";
import { ServiceRow } from "@App/Components/common/ServiceRow/ServiceRow";
import { ServiceRowGroups } from "@App/Components/common/ServiceRow/ServiceRowGroups";
import { ServiceRowGroupsSkeleton } from "@App/Components/common/ServiceRow/ServiceRowGroupsSkeleton";
import { LocationFilter } from "@App/Components/Filters";
import { Hero } from "@App/Components/Hero";
import { HomeSections } from "@App/Components/molecules/whitelabel/home/sections/HomeSections";
import { Step } from "@App/Components/Stepper/Step";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { useServices } from "@App/hooks/api/useServices";
import { useHome } from "@App/hooks/useHome";
import { mahleServiceGroups } from "@App/Pages/Partner/Mahle/serviceGroups";
import type { State } from "@App/state";
import { getServiceIcon } from "@App/utils/getServiceIcon";
import { groupServices } from "@App/utils/groupServices";
import { Fragment, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

/** Below this many services a flat list is easier to scan than collapsible sections. */
const GROUPING_THRESHOLD = 8;

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const { t } = useTranslation();

  const organization = useSelector((state: State) => state.orders.organization);
  const service = useSelector((state: State) => state.orders.service);
  const place = useSelector((state: State) => state.orders.place);

  const { loading, error, services } = useServices();

  const { currentStep, setCurrentStep, selectService } = useHome();

  // Below the threshold the categories cost more than they save, so the rows stay flat — which also
  // covers a network that hands Mahle only a handful of services.
  const groups =
    services && services.length > GROUPING_THRESHOLD
      ? groupServices(services, mahleServiceGroups, t("Weitere Services"))
      : [];

  const submit = () => {
    onSubmit && onSubmit();
  };

  if (error) {
    console.log(error);
  }

  const serviceGridClasses =
    "ease-in-out grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 mt-4 w-full md:w-fit";

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service && service.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <Fragment>
              {loading && (
                <div className="overflow-hidden">
                  <ServiceRowGroupsSkeleton count={mahleServiceGroups.length} />
                </div>
              )}
              {error && <div>{t("error")}</div>}
              {!loading && groups.length > 0 && (
                <div className="overflow-hidden">
                  <ServiceRowGroups
                    groups={groups}
                    onSelect={(selected) => selectService(selected, false)}
                  />
                </div>
              )}
              {!loading && services && groups.length === 0 && (
                <div className="overflow-hidden">
                  <div className={serviceGridClasses}>
                    {services.map((service) => (
                      <ServiceRow
                        key={service.id}
                        title={
                          service.pivot.display_name ? service.pivot.display_name : service.name
                        }
                        onClick={() => selectService(service, false)}
                        subtitle={service.pivot.subtitle}
                        icon={getServiceIcon(service.pivot.icon)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </Fragment>
          </Step>
          <Step
            title={t("Waehlen Sie Ihren Wunschort.")}
            completedTitle={""}
            completedGoBackLinkTitle={service ? service.name : ""}
          >
            <Fragment>
              <div className={`pb-32 md:pb-20`}>
                <div className={`md:w-80`}>
                  <LocationFilter className="relative" onSubmit={submit} />
                </div>
                <div
                  onClick={submit}
                  className={`${!service || (!place || !place.lat) ? "cursor-pointer pointer-events-none no-underline" : ""}`}
                >
                  <div
                    className={`${!service || !place ? "bg-primary-100" : "bg-primary"} w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline`}
                  >
                    {t("Werkstatt suchen")}
                  </div>
                </div>
              </div>
            </Fragment>
          </Step>
        </Stepper>
        <div className="mt-24">
          <HomeSections />
        </div>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <div className="md:pr-12">
                <div className="font-serif text-2xl mb-4">{t("benefits.title")}</div>
                <BulletPoint
                  text={t("benefits.bullet-point-1", {
                    organization: organization.name,
                  })}
                >
                  <CalendarCheckBenefitsIcon />
                </BulletPoint>
                <BulletPoint
                  text={t("benefits.bullet-point-2", {
                    organization: organization.name,
                  })}
                >
                  <CheckBenefitsIcon />
                </BulletPoint>
                <BulletPoint
                  text={t("benefits.bullet-point-3", {
                    organization: organization.name,
                  })}
                >
                  <BonusBenefitsIcon />
                </BulletPoint>
              </div>
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
